import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Users, Compass, Eye, Layers, ChevronDown } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { ViewModeSelector, ViewModeButton } from '../../components/ViewModeSelector';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  height: 100%;
  overflow-y: auto;
`;

const SectionLabel = styled.div`
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 600;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;

  svg { width: 12px; height: 12px; opacity: 0.5; }
`;

const InstanceName = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: white;
`;

const Description = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
  margin-top: 6px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 4px;

  svg { width: 13px; height: 13px; opacity: 0.4; }
`;

const PerspectiveBadge = styled.div`
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(255, 170, 0, 0.1);
  border: 1px solid rgba(255, 170, 0, 0.25);
`;

const PerspectiveTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 170, 0, 0.9);
`;

const PerspectiveDesc = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
  margin-top: 4px;
`;

const ScenarioCard = styled.div`
  padding: 10px 14px;
  border-radius: 10px;
  background: ${p => p.$active ? 'rgba(91, 157, 217, 0.12)' : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${p => p.$active ? 'rgba(91, 157, 217, 0.3)' : 'rgba(255, 255, 255, 0.06)'};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${p => p.$active ? 'rgba(91, 157, 217, 0.16)' : 'rgba(255, 255, 255, 0.06)'};
  }
`;

const ScenarioTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${p => p.$active ? '#5B9DD9' : 'rgba(255, 255, 255, 0.7)'};
`;

const ScenarioDesc = styled.div`
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 3px;
  line-height: 1.4;
  ${p => p.$expanded ? '' : `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const ExpandChevron = styled(ChevronDown)`
  flex-shrink: 0;
  margin-left: auto;
  color: rgba(255, 255, 255, 0.4);
  transition: transform 0.2s;
  transform: rotate(${p => p.$expanded ? '180deg' : '0deg'});
`;

const ActiveBadge = styled.span`
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #5B9DD9;
  background: rgba(91, 157, 217, 0.15);
  padding: 1px 6px;
  border-radius: 4px;
  margin-left: 8px;
`;

const ModeCard = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(33, 150, 243, 0.08);
  border: 1px solid rgba(33, 150, 243, 0.2);
`;

const ModeTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: #cfe4fa;
`;

const ModeDesc = styled.div`
  margin-top: 4px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.58);
`;

const Legend = styled.span`
  display: inline-block;
  margin-top: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid #5B9DD9;
  color: #9bc4eb;
  font-size: 0.68rem;
`;

const ActionRow = styled.div`
  margin-top: 10px;
`;

/**
 * InfoPanel — "Where am I?" context panel for workshop participants.
 * Shows workshop name, description, scenarios, active scenario, and perspective.
 */
export default function InfoPanel({ contextRef }) {
  const { t } = useTranslation();
  const [expandedKey, setExpandedKey] = useState(null);
  const {
    instanceTitle,
    instanceDescription,
    scenarios,
    scenarioInfo,
    currentViewMode,
    onViewModeChange,
    selectedPerspective,
    participantCount
  } = contextRef.current;

  // Convert scenarios object { scenarioKey: { title, description, variables } } to array
  const scenarioList = scenarios ? Object.entries(scenarios).map(([key, s]) => ({
    key,
    title: s.title || key,
    description: s.description || '',
  })) : [];

  return (
    <Container>
      {/* Instance */}
      <div>
        <SectionLabel>{t('impactPath.infoPanel.workshop')}</SectionLabel>
        <InstanceName>{instanceTitle || '—'}</InstanceName>
        {instanceDescription && (
          <Description>{instanceDescription.length > 200 ? instanceDescription.substring(0, 200) + '...' : instanceDescription}</Description>
        )}
        {participantCount > 0 && (
          <MetaRow><Users /> {t('impactPath.infoPanel.participants', { count: participantCount })}</MetaRow>
        )}
      </div>

      {/* Aiheenrajaus */}
      {selectedPerspective && (
        <div>
          <SectionLabel><Eye /> {t('impactPath.infoPanel.perspectiveLabel')}</SectionLabel>
          <PerspectiveBadge>
            <PerspectiveTitle>
              {selectedPerspective.custom_title || selectedPerspective.title}
            </PerspectiveTitle>
            {selectedPerspective.description && (
              <PerspectiveDesc>{selectedPerspective.description}</PerspectiveDesc>
            )}
          </PerspectiveBadge>
        </div>
      )}

      <div>
        <SectionLabel><Layers /> {t('impactPath.viewMode.introTitle')}</SectionLabel>
        <ModeCard>
          <ModeTitle>
            {currentViewMode === 'individual'
              ? t('impactPath.viewMode.mineTitle')
              : currentViewMode === 'scenario'
                ? t('impactPath.viewMode.scenarioTitle')
                : t('impactPath.viewMode.allTitle')}
          </ModeTitle>
          <ModeDesc>
            {currentViewMode === 'individual'
              ? t('impactPath.viewMode.mineDescription')
              : currentViewMode === 'scenario'
                ? t('impactPath.viewMode.scenarioDescription')
                : t('impactPath.viewMode.allDescription')}
          </ModeDesc>
          {currentViewMode !== 'individual' && (
            <Legend>{t('impactPath.viewMode.ownNodeLegend')}</Legend>
          )}
          <ActionRow>
            <ViewModeSelector>
              <ViewModeButton
                $active={currentViewMode === 'individual'}
                onClick={() => onViewModeChange?.('individual')}
              >
                {t('impactPath.viewMode.mineLabel')}
              </ViewModeButton>
              <ViewModeButton
                $active={currentViewMode === 'scenario'}
                onClick={() => onViewModeChange?.('scenario')}
              >
                {t('impactPath.viewMode.scenarioLabel')}
              </ViewModeButton>
              <ViewModeButton
                $active={currentViewMode === 'instance'}
                onClick={() => onViewModeChange?.('instance')}
              >
                {t('impactPath.viewMode.allLabel')}
              </ViewModeButton>
            </ViewModeSelector>
          </ActionRow>
        </ModeCard>
      </div>

      {/* Scenarios */}
      {scenarioList.length > 0 && (
        <div>
          <SectionLabel><Compass /> {t('impactPath.infoPanel.scenarios', { count: scenarioList.length })}</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {scenarioList.map(s => {
              const isActive = scenarioInfo?.title === s.title;
              const expanded = expandedKey === s.key;
              return (
                <ScenarioCard
                  key={s.key}
                  $active={isActive}
                  onClick={() => setExpandedKey(expanded ? null : s.key)}
                >
                  <ScenarioTitle $active={isActive}>
                    {s.title}
                    {isActive && <ActiveBadge>{t('impactPath.infoPanel.active')}</ActiveBadge>}
                    {s.description && <ExpandChevron size={15} $expanded={expanded} />}
                  </ScenarioTitle>
                  {s.description && <ScenarioDesc $expanded={expanded}>{s.description}</ScenarioDesc>}
                </ScenarioCard>
              );
            })}
          </div>
        </div>
      )}
    </Container>
  );
}
